<ng-container *transloco="let t; prefix: 'events-widget'">

  @let readingSessionCount = activeReadingSessions().size;

  <button #toggle type="button" class="btn btn-icon"
          [ngbPopover]="popContent" [title]="t('title-alt')" [attr.aria-label]="buttonAltLabel()"
          placement="bottom-end" [popoverClass]="'nav-events'"
          [autoClose]="'outside'" container="body" (shown)="onShown()" (hidden)="isOpen.set(false)">
    <app-events-widget-icon
      [runningProgress]="runningProgress()"
      [indeterminate]="indeterminate()"
      [attentionCount]="attentionCount()"
      [hasError]="hasError()"
      [offline]="offline()"
      [readingCount]="readingSessionCount" />
  </button>

  <div class="visually-hidden" aria-live="polite">{{announcement()}}</div>

  <ng-template #popContent>
    <div class="activity d-flex flex-column" tabindex="-1" role="dialog" [attr.aria-label]="t('title-alt')"
         (keydown.escape)="closeAndRefocus()">
      @if (offline()) {
        <div class="offline d-flex align-items-center gap-2">
          <i class="fa-solid fa-plug-circle-xmark" aria-hidden="true"></i>
          <span class="flex-grow-1">{{t('server-unreachable-label')}}</span>
        </div>
      }

      <div class="header d-flex flex-column gap-2">
        <div class="d-flex justify-content-between align-items-center">
          <span class="fw-semibold">{{t('title-label')}}</span>
          @if (hasFinished()) {
            <button type="button" class="btn btn-link link p-0" (click)="activityStore.clearFinished()">{{t('clear-finished-label')}}</button>
          }
        </div>
        <div class="d-flex flex-wrap gap-2" role="group" [attr.aria-label]="t('filter-group-alt')">
          <button type="button" class="chip d-flex align-items-center gap-1" [class.active]="filter() === ActivityFilter.All"
                  [attr.aria-pressed]="filter() === ActivityFilter.All" (click)="filter.set(ActivityFilter.All)">
            {{t('filter-all-label')}}
          </button>
          <button type="button" class="chip d-flex align-items-center gap-1" [class.active]="filter() === ActivityFilter.Attention"
                  [attr.aria-pressed]="filter() === ActivityFilter.Attention" (click)="filter.set(ActivityFilter.Attention)">
            {{t('filter-attention-label')}}
            @if (attentionChipCount() > 0) {
              <span class="count">{{attentionChipCount()}}</span>
            }
          </button>
          <button type="button" class="chip d-flex align-items-center gap-1" [class.active]="filter() === ActivityFilter.Jobs"
                  [attr.aria-pressed]="filter() === ActivityFilter.Jobs" (click)="filter.set(ActivityFilter.Jobs)">
            {{t('filter-jobs-label')}}
            @if (jobsChipCount() > 0) {
              <span class="count">{{jobsChipCount()}}</span>
            }
          </button>
        </div>
      </div>

      <div class="body flex-grow-1">
        @if (showPinned()) {
          <div class="pinned d-flex flex-column gap-1">
            @for (job of interruptedJobs(); track job.id) {
              <app-activity-job-row [job]="job" [now]="now()" [libraryNames]="libraryNames()" [pinned]="true"
                                    (dismissed)="activityStore.dismiss($event)" />
            }
            @for (entry of pinned(); track entry.id) {
              <app-activity-entry-row [entry]="entry" [now]="now()" [libraryNames]="libraryNames()" [pinned]="true"
                                      (dismissed)="activityStore.dismiss($event)" (navigated)="close()" />
            }
          </div>
        }

        <div class="timeline">
          @for (item of visibleTimeline(); track item.id) {
            @switch (item.kind) {
              @case (ActivityRowKind.Job) {
                <app-activity-job-row [job]="item" [now]="now()" [libraryNames]="libraryNames()" (navigated)="close()" />
              }
              @case (ActivityRowKind.Entry) {
                <app-activity-entry-row [entry]="item" [now]="now()" [libraryNames]="libraryNames()"
                                        (dismissed)="activityStore.dismiss($event)" (navigated)="close()" />
              }
              @case ('group') {
                @let isGroupOpen = openGroups().has(item.id);
                <div class="group d-flex gap-3">
                  <div class="time flex-shrink-0">{{item.updatedUtc | activityAge : now()}}</div>
                  <div class="d-flex align-items-center gap-2 flex-grow-1">
                    <span class="dot flex-shrink-0"></span>
                    <span class="flex-grow-1">{{t('problems-in-libraries-label', {count: item.libraryCount})}}</span>
                    <button type="button" class="btn btn-link link p-0 flex-shrink-0" [attr.aria-expanded]="isGroupOpen" (click)="toggleGroup(item.id)">
                      {{EventAction.Details | eventAction}}
                    </button>
                  </div>
                </div>
                @if (isGroupOpen) {
                  <div class="group-entries">
                    @for (entry of item.entries; track entry.id) {
                      <app-activity-entry-row [entry]="entry" [now]="now()" [libraryNames]="libraryNames()"
                                              (dismissed)="activityStore.dismiss($event)" (navigated)="close()" />
                    }
                  </div>
                }
              }
            }
          } @empty {
            @if (!showPinned()) {
              <div class="empty text-center">{{filter() === ActivityFilter.All ? t('no-data-description') : t('filter-empty-label')}}</div>
            }
          }
        </div>

        @if (showUpNext()) {
          <app-activity-up-next [snapshot]="snapshot()!" [delayedEntries]="delayedEntries()" [libraryNames]="libraryNames()" />
        }
      </div>

      <div class="footer d-flex align-items-center gap-2">
        <span class="flex-grow-1">
          @if (readingSessionCount > 0) {
            {{t('reading-now', {num: readingSessionCount})}}
          }
        </span>
        <a class="link" [routerLink]="['/settings']" [fragment]="SettingsTabId.Activity" (click)="close()">{{t('view-activity-label')}}</a>
      </div>
    </div>
  </ng-template>
</ng-container>
